import React, { FunctionComponent, Suspense, useEffect } from 'react';

import { CREATE_MODAL_TYPES } from '@savgroup-front-common/constants';
import { SwipeableDrawer } from '@savgroup-front-common/core/atoms/SwipeableDrawer';
import { retryImportChunk } from '@savgroup-front-common/core/helpers/retryImportChunk';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import {
  RUM_ACTION_NAME,
  trackRumAction,
} from '@savgroup-front-common/core/services';
import { Skeleton } from '@savgroup-front-common/primitive/ui/skeleton';

const CreateSeller = React.lazy(() =>
  retryImportChunk(() => import('../../SellerManagement/CreateSeller')),
);
const CreateTenant = React.lazy(() =>
  retryImportChunk(() => import('../../SellerManagement/CreateTenant')),
);
const CreateFileByProductReference = React.lazy(() =>
  retryImportChunk(
    () => import('../../CreateFile/CreateFileByProductReference'),
  ),
);
const CreateFileByCustomerInformation = React.lazy(() =>
  retryImportChunk(
    () => import('../../CreateFile/CreateFileByCustomerInformation'),
  ),
);
const CreateFileByOrderReference = React.lazy(() =>
  retryImportChunk(() => import('../../CreateFile/CreateFileByOrderReference')),
);
const CreateFileByReceiptInformation = React.lazy(() =>
  retryImportChunk(
    () => import('../../CreateFile/CreateFileByReceiptInformation'),
  ),
);
const CreateFileByOwnerStock = React.lazy(() =>
  retryImportChunk(() => import('../../CreateFile/CreateFileByOwnerStock')),
);

interface Props {
  modalType: string | null;
  onClose: () => void;
}

const CreateModal: FunctionComponent<React.PropsWithChildren<Props>> = ({
  modalType,
  onClose,
}) => {
  useEffect(() => {
    if (modalType) {
      trackRumAction(RUM_ACTION_NAME.OPEN_OLD_CREATE_FILE, {
        modalType,
      });
    }
  }, [modalType]);

  return (
    <Suspense fallback={<BaseLoader />}>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.FILE_ORDER_REFERENCE}
        dataTestId={CREATE_MODAL_TYPES.FILE_ORDER_REFERENCE}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense fallback={<BaseLoader />}>
          <CreateFileByOrderReference onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.FILE_PRODUCT_REFERENCE}
        dataTestId={CREATE_MODAL_TYPES.FILE_PRODUCT_REFERENCE}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense fallback={<BaseLoader />}>
          <CreateFileByProductReference onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.FILE_BY_CUSTOMER_INFORMATION}
        dataTestId={CREATE_MODAL_TYPES.FILE_BY_CUSTOMER_INFORMATION}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense fallback={<BaseLoader />}>
          <CreateFileByCustomerInformation onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.FILE_OWNER_STOCK}
        dataTestId={CREATE_MODAL_TYPES.FILE_OWNER_STOCK}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense fallback={<BaseLoader />}>
          <CreateFileByOwnerStock onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.FILE_RECEIPT_INFORMATION}
        dataTestId={CREATE_MODAL_TYPES.FILE_RECEIPT_INFORMATION}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense fallback={<BaseLoader />}>
          <CreateFileByReceiptInformation onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>

      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.SELLER}
        dataTestId={CREATE_MODAL_TYPES.SELLER}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense
          fallback={
            <>
              <Skeleton className="mb-5 h-12 w-full" />
              <Skeleton className="h-36 w-full" />
            </>
          }
        >
          <CreateSeller onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
      <SwipeableDrawer
        isOpen={modalType === CREATE_MODAL_TYPES.TENANT}
        dataTestId={CREATE_MODAL_TYPES.TENANT}
        onClose={onClose}
        hasBackdrop
        hasCloseOnBackdrop={false}
      >
        <Suspense
          fallback={
            <>
              <Skeleton className="mb-5 h-12 w-full" />
              <Skeleton className="h-36 w-full" />
            </>
          }
        >
          <CreateTenant onClose={onClose} />
        </Suspense>
      </SwipeableDrawer>
    </Suspense>
  );
};

export default CreateModal;
